import { ArticleComponentCredits } from "@gik/blog/components/ArticleComponentCredits/ArticleComponentCredits";
import { BuyingGuide } from "@gik/blog/components/BuyingGuide/BuyingGuide";
import { GalleryWithLightbox } from "@gik/blog/components/GalleryWithLightbox/GalleryWithLightbox";
import { ProductCarousel } from "@gik/blog/components/ProductCarousel/ProductCarousel";
import { RecipeCard } from "@gik/blog/components/RecipeCard/RecipeCard";
import type Article from "@gik/blog/models/Article";
import bemBlock from "@gik/core/utils/bemBlock";
import { EmbeddedVideo } from "@gik/ui-legacy/EmbeddedVideo";
import { PageSection } from "@gik/ui-legacy/gik/PageSection";
import { HTMLParser } from "@gik/ui-legacy/HTMLParser";
import { KeepProportions } from "@gik/ui-legacy/KeepProportions";
import type { DomElement } from "htmlparser2";
import React from "react";

export interface IArticleBodyProps {
	article: Article;
}

const blockName = "article-body";
const maxGalleries = 3;

export function ArticleBody({ article }: IArticleBodyProps) {
	function replace(node: DomElement) {
		const bem = bemBlock(blockName);

		if (
			node.type == "tag" &&
			node.name == "wp-gallery" &&
			node.attribs["index"]
		) {
			if (article.customContentLayoutTypes.indexOf("galleries") < 0) {
				return null;
			}
			const galleryIndex = parseInt(node.attribs["index"]) - 1;
			if (galleryIndex >= 0 && galleryIndex < article.galleries.length) {
				const gallery = article.galleries[galleryIndex];
				const photos = gallery.photos.map((photo, imageIndex) => ({
					key: imageIndex.toString(),
					src: photo?.src,
					width: photo?.width,
					height: photo?.height,
					caption: photo?.caption,
					credit: photo?.credit,
					alt: photo?.caption,
				}));

				return (
					<GalleryWithLightbox
						photos={photos}
						caption={gallery.caption}
						credit={gallery.credit}
						creditLink={gallery.creditLink}
					/>
				);
			}
		}
		if (node.type == "tag" && node.name == "wp-youtube") {
			return (
				<div className={bem("youtube-wrapper")}>
					<KeepProportions>
						<EmbeddedVideo url={node.attribs["url"]} lightboxOverlay />
					</KeepProportions>
					<ArticleComponentCredits
						caption={node.attribs["caption"]}
						credit={node.attribs["credit"]}
						creditLink={node.attribs["credit-link"]}
					/>
				</div>
			);
		}
		if (node.type == "tag" && node.name == "wp-blockquote") {
			const attribution = node.attribs["attribution"];
			return (
				<div
					className={`blockquote justify-${node.attribs["justify"] || "left"}`}
				>
					<div className={bem("content")}>
						<p>
							“
							{node.children.map((child: DomElement, childIndex: number) => {
								if (child.children) {
									const Component = child.name as keyof JSX.IntrinsicElements;
									return (
										<Component key={childIndex}>
											{child.children?.[0]?.data}
										</Component>
									);
								} else {
									return <span key={childIndex}>{child.data}</span>;
								}
							})}
							”
						</p>
					</div>
					{attribution && (
						<div className={bem("attribution")}>
							<p>– {attribution}</p>
						</div>
					)}
				</div>
			);
		}
		if (node.type == "tag" && node.name == "wp-recipe") {
			if (article.customContentLayoutTypes.indexOf("recipe") < 0) {
				return null;
			}
			if (article.recipe) {
				return <RecipeCard recipe={article.recipe} />;
			}
		}
		if (node.type == "tag" && node.name == "wp-product-bundle") {
			return (
				<ProductCarousel
					title={node.attribs["title"] || "Helpful Products"}
					productLinks={article.productBundle}
				/>
			);
		}

		if (node.type == "tag" && node.name == "wp-buying-guide") {
			if (article.customContentLayoutTypes.indexOf("buying-guide") < 0) {
				return null;
			}
			return (
				<PageSection>
					<BuyingGuide productLinks={article.buyingGuide} />
				</PageSection>
			);
		}

		return undefined; // use default value
	}

	const html: string = React.useMemo(() => {
		const bem = bemBlock(blockName);

		let html = `<div id="article-body" class="${bem()}">`;
		html += article.content;

		// add leading style to the first paragraph
		html = html.replace("<p>", `<p class="${bem("lead")}">`);

		// add specific class to all WP images
		html = html.replace(/<img.+?class="/g, (match) => match + "wp-image ");

		// add specific class to all WP links
		html = html.replace(/<a href=/g, '<a class="wp-link" href=');

		// shortcode replacement
		html = html.replace(
			/\[masonry-gallery ([\s\S]+?)]/g,
			"<wp-gallery $1></wp-gallery>",
		);
		html = html.replace(
			/\[youtube ([\s\S]+?)]/g,
			"<wp-youtube $1></wp-youtube>",
		);
		html = html.replace(
			/\[block-quote ([\s\S]+?)]([\s\S]+?)\[\/block-quote]/g,
			"<wp-blockquote $1>$2</wp-blockquote>",
		);
		html = html.replace(/\[recipe]/g, "<wp-recipe></wp-recipe>");
		html = html.replace(
			/\[product-bundle([\s\S]+?)]/g,
			"<wp-product-bundle$1></wp-product-bundle>",
		);
		html = html.replace(
			/\[buying-guide]/g,
			"<wp-buying-guide></wp-buying-guide>",
		);

		// remove WP-generated slashes that kill shortcode attributes
		// TODO: Replace within the shortcodes rather than the whole article? or is there a WP solution?
		html = html.replace(/&#8221;/g, '"');
		html = html.replace(/&#8243;/g, '"');

		// Inject mandatory components at the bottom of the post if they haven't been added as shortcodes
		// [Recipe, Helpful Products, Buying Guide, Galleries(1,2,3)]
		// TODO: Helpful products and buying guide
		if (
			article.recipe &&
			html.indexOf("<wp-recipe>") < 0 &&
			article.customContentLayoutTypes.indexOf("recipe") >= 0
		) {
			html += "<wp-recipe></wp-recipe>";
		}
		for (let i = 1; i <= maxGalleries; i++) {
			if (
				article.galleries.length >= i &&
				html.indexOf(`<wp-gallery index="${i}"`) < 0 &&
				article.customContentLayoutTypes.indexOf("galleries") >= 0
			) {
				html += `<wp-gallery index="${i}"></wp-gallery>`;
			}
		}
		if (article.productBundle && html.indexOf("<wp-product-bundle") < 0) {
			html += "<wp-product-bundle></wp-product-bundle>";
		}
		if (
			article.buyingGuide &&
			html.indexOf("<wp-buying-guide") < 0 &&
			article.customContentLayoutTypes.indexOf("buying-guide") >= 0
		) {
			html += "<wp-buying-guide></wp-buying-guide>";
		}

		html += "</div>";

		return html;
	}, [article]);

	return (
		<HTMLParser
			rawHtml={html}
			allowFormattingTags
			options={{
				replace,
			}}
		/>
	);
}
